import { useLanguage } from '@base/LanguageProvider';
import { TagType } from '@common/api';
import { useTagContext } from '@data/tag/tagContext';
import EditTag from '@shared/finder/modals/EditTag';
import Manager from '@shared/modal/Manager';
import { TagBadge } from '@styled/Basic';
import { ExpList, ExpListItem, TypeaheadContainer, TypeaheadDetails, TypeaheadFilter } from '@styled/Expression';
import { FlexRow } from '@styled/FlexWrappers';
import { ExpressionBlockTypes } from '@util/enums';
import { Editor } from 'draft-js';
import React, { useEffect, useRef } from 'react';
import FSButton from '../../components/FSButton';
import { useSheetRowsById } from '../../data/sheetRow/sheetRowHooks';
import { getTagParents, getTagTypeName } from '../../data/tag/tagUtil';
import { isAccountTag, isDimensionTag, isGroupTag, isTableTag } from '../../data/tag/types';
import { appColors } from '../../styled/useTheme';
import Tag from '../tags/Tag';
import { getEditorState } from './ExpEditor';
function genBadge(item, L) {
    switch (item === null || item === void 0 ? void 0 : item.blockType) {
        case ExpressionBlockTypes.LITERAL:
            return React.createElement(TagBadge, { className: "transparent" }, L('literal'));
        case ExpressionBlockTypes.FUNCTION:
            return React.createElement(TagBadge, { className: "func" }, L('function'));
        case ExpressionBlockTypes.TAG:
            return item.type ? (React.createElement(TagBadge, { className: item.type === TagType.Account
                    ? 'acc'
                    : item.type === TagType.Table
                        ? 'table'
                        : item.type === TagType.Group
                            ? 'transparent'
                            : '' }, L(getTagTypeName(item).toLowerCase()))) : null;
    }
}
function Typeahead(_a) {
    var _b, _c;
    var items = _a.items, invert = _a.invert, filter = _a.filter, selectedIndex = _a.selectedIndex, arrowDirection = _a.arrowDirection, clickHandler = _a.clickHandler;
    var L = useLanguage().L;
    var typeAheadRef = useRef(null);
    var tagData = useTagContext().tagData;
    var sheetRowsById = useSheetRowsById();
    useEffect(function () {
        Manager.closePopover();
        var scrollTo = function (scrollTop, reset) {
            if (typeAheadRef && typeAheadRef.current) {
                typeAheadRef.current.scrollTop = reset ? scrollTop : typeAheadRef.current.scrollTop + scrollTop;
            }
        };
        if (selectedIndex === 0) {
            scrollTo(0, true);
        }
        else if (selectedIndex + 1 === items.length) {
            scrollTo(items.length * 45, false);
        }
        else if (selectedIndex > 4) {
            scrollTo(arrowDirection * 45, false);
        }
    }, [selectedIndex]);
    function openInTagEditor() {
        var _a, _b;
        var parentTag = tagData.tagById[(_a = tag === null || tag === void 0 ? void 0 : tag.ParentId) !== null && _a !== void 0 ? _a : 0];
        if (tag) {
            Manager.closeModal();
            window.location.hash = "/database/".concat(isAccountTag(tag)
                ? tag.ParentId === null
                    ? 0
                    : tag.ParentId
                : isDimensionTag(tag)
                    ? (_b = parentTag === null || parentTag === void 0 ? void 0 : parentTag.ParentId) !== null && _b !== void 0 ? _b : 0
                    : tag.ParentId, "/").concat(isAccountTag(tag) || isGroupTag(tag) ? 0 : isTableTag(tag) ? tag.id : tag.ParentId);
        }
    }
    function openTagModal() {
        Manager.renderModal(React.createElement(EditTag, { tag: tag }));
    }
    var activeItem = items === null || items === void 0 ? void 0 : items[selectedIndex];
    var tag = (activeItem === null || activeItem === void 0 ? void 0 : activeItem.id) && (activeItem === null || activeItem === void 0 ? void 0 : activeItem.blockType) === ExpressionBlockTypes.TAG
        ? tagData.tagById[(_b = activeItem === null || activeItem === void 0 ? void 0 : activeItem.id) !== null && _b !== void 0 ? _b : 0]
        : undefined;
    var parents = tag ? getTagParents(tag, tagData.tagById) : [];
    var pathContent = parents.map(function (t, idx) {
        var _a, _b;
        return (React.createElement("span", { key: (_a = t === null || t === void 0 ? void 0 : t.id) !== null && _a !== void 0 ? _a : 0 },
            React.createElement("span", { style: {
                    color: isAccountTag(t)
                        ? appColors.secondary.w500
                        : isTableTag(t)
                            ? appColors.primary.w500
                            : appColors.ui.dark,
                } }, " ".concat(!(t === null || t === void 0 ? void 0 : t.name) && idx === 0 ? L('Root') : (_b = t === null || t === void 0 ? void 0 : t.name) !== null && _b !== void 0 ? _b : 'NOT_FOUND', " ")),
            "/"));
    });
    if (isAccountTag(tag)) {
        pathContent.push(React.createElement("span", { key: 69420 },
            React.createElement("span", { style: {
                    color: appColors.secondary.w500,
                } }, " ".concat(L('Variables'), " "))));
    }
    var dimensionsContent = (_c = tag === null || tag === void 0 ? void 0 : tag.dimensionTagIds) === null || _c === void 0 ? void 0 : _c.filter(function (d) { return !!tagData.tagById[d]; }).map(function (d, idx, arr) {
        var table = tagData.tagById[d];
        return (React.createElement(Tag, { key: d, tag: table, canUpdate: false, style: { marginRight: idx !== arr.length - 1 ? 8 : 0, marginBottom: 5 } }));
    });
    return (React.createElement(TypeaheadContainer, { style: { top: invert ? -300 : 0 } },
        React.createElement(TypeaheadFilter, null,
            L('Filter'),
            ": ",
            React.createElement("span", null, "\"".concat(filter, "\""))),
        React.createElement(FlexRow, { style: { alignItems: 'stretch' } },
            React.createElement(ExpList, { id: "typeaheadContainer", ref: typeAheadRef, style: { width: '50%' } }, items.map(function (item, idx) {
                return (React.createElement(ExpListItem, { key: "".concat(item.blockType, "-").concat(item.id), onMouseDown: function () { return clickHandler(idx); }, className: idx === selectedIndex ? 'active' : '' },
                    React.createElement("span", null, "".concat(item.title).slice(0, 33)),
                    genBadge(item, L)));
            })),
            React.createElement(TypeaheadDetails, { style: { width: '50%' } },
                React.createElement("div", null,
                    React.createElement("div", { style: {
                            display: 'flex',
                            paddingBottom: 10,
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        } },
                        (activeItem === null || activeItem === void 0 ? void 0 : activeItem.title) && React.createElement("span", { className: "title" }, activeItem === null || activeItem === void 0 ? void 0 : activeItem.title),
                        React.createElement("span", { style: { display: 'flex', alignItems: 'center', justifyContent: 'end' } }, (activeItem === null || activeItem === void 0 ? void 0 : activeItem.blockType) === ExpressionBlockTypes.TAG ? (React.createElement(React.Fragment, null,
                            React.createElement(FSButton, { title: L('Open in Database Editor'), "$type": "tertiary", "$size": "small", onMouseDown: openInTagEditor }),
                            React.createElement(FSButton, { style: { marginLeft: 5 }, title: L('Edit'), "$type": "tertiary", "$size": "small", onMouseDown: openTagModal }))) : (genBadge(activeItem, L)))),
                    (activeItem === null || activeItem === void 0 ? void 0 : activeItem.blockType) === ExpressionBlockTypes.FUNCTION &&
                        (activeItem === null || activeItem === void 0 ? void 0 : activeItem.example) !== undefined && (React.createElement("div", null,
                        React.createElement("div", { className: "info" },
                            L('Example'),
                            ": "),
                        React.createElement("div", { className: "example" }, activeItem === null || activeItem === void 0 ? void 0 : activeItem.example))),
                    (activeItem === null || activeItem === void 0 ? void 0 : activeItem.description) && (React.createElement("div", null,
                        React.createElement("div", { className: "info" },
                            L('Description'),
                            ":"),
                        React.createElement("div", { className: "description" }, activeItem === null || activeItem === void 0 ? void 0 : activeItem.description))),
                    (activeItem === null || activeItem === void 0 ? void 0 : activeItem.blockType) === ExpressionBlockTypes.TAG && isAccountTag(tag) && (React.createElement(React.Fragment, null,
                        React.createElement("div", null,
                            React.createElement("div", { className: "info" },
                                L('Dimensions'),
                                ":"),
                            React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap' } }, (dimensionsContent === null || dimensionsContent === void 0 ? void 0 : dimensionsContent.length) ? dimensionsContent : 'None')),
                        (tag === null || tag === void 0 ? void 0 : tag.expression) && (React.createElement("div", null,
                            React.createElement("div", { className: "info" },
                                L('Formula'),
                                ":"),
                            React.createElement("div", { className: "expression" },
                                React.createElement(Editor, { readOnly: true, onChange: function () { }, editorState: getEditorState(tag.expression, tagData, sheetRowsById) })))))),
                    (activeItem === null || activeItem === void 0 ? void 0 : activeItem.blockType) === ExpressionBlockTypes.TAG && (React.createElement("div", null,
                        React.createElement("div", { className: "info" },
                            L('Path'),
                            ":"),
                        React.createElement("div", null, pathContent))))))));
}
export default Typeahead;
